import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Button,
} from '@mui/material';
import ErrorSnackbar from '../ErrorSnackbar';
import { historyManager } from '../../utils/historyManager';
import { rulesManager } from '../../utils/rulesManager';
import FilterSection from '../FilterSection';
import ChangeHistory from '../ChangeHistory';
import RuleModal from '../RuleModal';

// Import components
import TransactionGrid from './components/TransactionGrid';
import ActionPanel from './components/ActionPanel';

// Import utilities
import { saveTableData, loadTableData, loadFilters, saveAllDataToSupabase, loadAllDataFromSupabase } from './utils/storageUtils';
import { processTransactionData, filterTransactionData } from './utils/filterUtils';
import * as bulkActions from './utils/bulkActions';


const TagTransactions = ({ userEmail, userSettings }) => {
  // State declarations
  const [errorMessage, setErrorMessage] = useState('');
  const [showError, setShowError] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const [tableData, setTableData] = useState([]);
  const [allTags, setAllTags] = useState(new Set());
  const [isLoading, setIsLoading] = useState(true);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [history, setHistory] = useState([]);
  const [rules, setRules] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedHistoryEntry, setSelectedHistoryEntry] = useState(null);
  const [filters, setFilters] = useState({
    searchText: '',
    tags: [],
    tagInput: '',
    description: '',
    description2: '',
    comments: '',
    minDate: null,
    maxDate: null,
    filterStartDate: null,
    filterEndDate: null,
    minAmount: -Infinity,
    maxAmount: Infinity,
    filterMinAmount: -Infinity,
    filterMaxAmount: Infinity,
    tax: { true: true, false: true }, // Both true means show all, both false means show none
    showUntagged: false, // new filter for untagged transactions
  });

  // Helper function to save table data and filters
  const saveDataAndFilters = useCallback((data) => {
    saveTableData(data, filters);
  }, [filters]);

  // Initialize table data from localStorage only
  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      
      try {
        // Always load from localStorage
        const savedData = loadTableData();
        const savedFilters = loadFilters();
        
        if (savedData && savedData.length > 0) {
          // Process transactions
          const { processedData, minAmount, maxAmount, minDate, maxDate, allTags: tags } = 
            processTransactionData(savedData);
          
          setTableData(processedData);
          setAllTags(tags);
          
          // Update filters
          if (savedFilters) {
            setFilters(prevFilters => ({
              ...prevFilters,
              ...savedFilters,
              minAmount: minAmount,
              maxAmount: maxAmount,
              filterMinAmount: savedFilters.filterMinAmount ?? minAmount,
              filterMaxAmount: savedFilters.filterMaxAmount ?? maxAmount,
              minDate: minDate,
              maxDate: maxDate,
              filterStartDate: savedFilters.filterStartDate ?? minDate,
              filterEndDate: savedFilters.filterEndDate ?? maxDate
            }));
          } else {
            // Set default filter ranges based on data
            setFilters(prevFilters => ({
              ...prevFilters,
              minAmount: minAmount,
              maxAmount: maxAmount,
              filterMinAmount: minAmount,
              filterMaxAmount: maxAmount,
              minDate: minDate,
              maxDate: maxDate,
              filterStartDate: minDate,
              filterEndDate: maxDate
            }));
          }
          
          // Load rules and history from local managers
          const localRules = rulesManager.getRules();
          if (localRules && localRules.length > 0) {
            setRules(localRules);
          }
          
          const localHistory = historyManager.getHistory();
          if (localHistory && localHistory.length > 0) {
            setHistory(localHistory);
          }
        }
      } catch (error) {
        console.error('Error loading data:', error);
        setErrorMessage('Failed to load data: ' + error.message);
        setShowError(true);
      } finally {
        setIsLoading(false);
      }
    };
    
    loadData();
  }, [userEmail]);

  // Load saved filters
  useEffect(() => {
    const savedFilters = loadFilters();
    if (savedFilters) {
      setFilters(prevFilters => ({
        ...prevFilters,
        ...savedFilters
      }));
    }
  }, []);

  // Load history and rules
  useEffect(() => {
    historyManager.loadHistory();
    setHistory(historyManager.getHistory());
    setRules(rulesManager.getRules());
  }, []);

  // Handler for saving data to Supabase database
  const handleSaveTransactions = async () => {
    try {
      setIsLoading(true);
      
      // Load the full history and rules from managers
      const currentHistory = historyManager.getHistory();
      const currentRules = rulesManager.getRules();
      
      // Save to Supabase
      const result = await saveAllDataToSupabase(
        tableData,
        currentRules,
        currentHistory,
        filters,
        userSettings,
        {} // reportSettings - placeholder for now
      );
      
      setIsLoading(false);
      
      if (result.success) {
        setSuccessMessage('Data saved successfully to your Supabase account!');
        setShowSuccess(true);
      } else {
        setErrorMessage(`Some errors occurred: ${result.errors.map(err => err.component).join(', ')}`);
        setShowError(true);
      }
    } catch (error) {
      setIsLoading(false);
      setErrorMessage(error.message || 'Failed to save data');
      setShowError(true);
    }
  };

  // Handler for loading data from Supabase database
  const handleLoadData = async () => {
    try {
      setIsLoading(true);
      
      const userData = await loadAllDataFromSupabase();
      
      if (userData && userData.transactions && userData.transactions.length > 0) {
        // Load transactions data
        const { processedData, minAmount, maxAmount, minDate, maxDate, allTags: tags } = 
          processTransactionData(userData.transactions);
        
        setTableData(processedData);
        setAllTags(tags);
        
        // Update filters with loaded data
        if (userData.filters) {
          setFilters(prevFilters => ({
            ...prevFilters,
            ...userData.filters,
            minAmount,
            maxAmount,
            minDate,
            maxDate
          }));
          localStorage.setItem("tableFilters", JSON.stringify(userData.filters));
        }
        
        // Load rules and history via managers
        if (userData.rules) {
          rulesManager.setRules(userData.rules);
          setRules(rulesManager.getRules());
        }
        
        if (userData.history) {
          historyManager.setHistory(userData.history);
          setHistory(historyManager.getHistory());
        }
        
        // Save transaction data to localStorage
        localStorage.setItem("transactionData", JSON.stringify(userData.transactions));
        
        setSuccessMessage('Data loaded successfully from your Supabase account!');
        setShowSuccess(true);
      } else {
        setErrorMessage('No data found in your account');
        setShowError(true);
      }
    } catch (error) {
      setErrorMessage(error.message || 'Failed to load data');
      setShowError(true);
    } finally {
      setIsLoading(false);
    }
  };

  // Receipt upload and OCR processing function
  const handleReceiptUpload = async (file) => {
    console.log('Starting receipt OCR processing...', file.name);
    setIsLoading(true);
    
    try {
      // Create FormData to send to Asprise Receipt OCR API
      const formData = new FormData();
      formData.append('api_key', 'TEST'); // Use 'TEST' for testing
      formData.append('recognizer', 'auto'); // can be 'US', 'CA', 'JP', 'SG' or 'auto'
      formData.append('ref_no', `receipt_${Date.now()}`); // optional reference
      formData.append('file', file);

      console.log('Uploading receipt to Asprise OCR API...');
      
      // Call Asprise Receipt OCR API
      const response = await fetch('https://ocr.asprise.com/api/v1/receipt', {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        throw new Error(`OCR API request failed: ${response.status} ${response.statusText}`);
      }

      const ocrResult = await response.json();
      console.log('Raw OCR API Response:', ocrResult);
      
      // Process the OCR result
      processReceiptOcrResult(ocrResult);
      
    } catch (error) {
      console.error('Error processing receipt:', error);
      setErrorMessage('Failed to process receipt: ' + error.message);
      setShowError(true);
    } finally {
      setIsLoading(false);
    }
  };

  // Process the structured OCR result from Asprise API
  const processReceiptOcrResult = (ocrResult) => {
    console.log('Processing Asprise OCR result...');
    
    if (!ocrResult || ocrResult.success !== true) {
      console.error('OCR processing failed:', ocrResult);
      setErrorMessage('OCR processing failed. Please try a clearer image.');
      setShowError(true);
      return;
    }

    // Extract receipt data from the structured response
    const receipts = ocrResult.receipts || [];
    
    if (receipts.length === 0) {
      console.warn('No receipts detected in the image');
      setErrorMessage('No receipts detected in the image. Please try a clearer receipt image.');
      setShowError(true);
      return;
    }

    // Process each detected receipt
    receipts.forEach((receipt, index) => {
      console.log(`Receipt ${index + 1} details:`, receipt);
      
      const receiptData = {
        merchant_name: receipt.merchant_name || 'Unknown Merchant',
        date: receipt.date || '',
        time: receipt.time || '',
        total: receipt.total || '',
        subtotal: receipt.subtotal || '',
        tax: receipt.tax || '',
        items: receipt.items || [],
        raw_text: receipt.raw_text || '',
        merchant_address: receipt.merchant_address || '',
        merchant_phone: receipt.merchant_phone || ''
      };

      console.log('Parsed receipt data:', receiptData);
      
      // Log individual items if available
      if (receiptData.items && receiptData.items.length > 0) {
        console.log('Receipt items:');
        receiptData.items.forEach((item, itemIndex) => {
          console.log(`  Item ${itemIndex + 1}:`, {
            description: item.description || 'N/A',
            amount: item.amount || 'N/A',
            qty: item.qty || 'N/A'
          });
        });
      }

      // You can extend this to automatically create a transaction
      // const transactionData = convertReceiptToTransaction(receiptData);
      // addNewTransaction(transactionData);
    });

    const totalReceipts = receipts.length;
    setSuccessMessage(`Successfully processed ${totalReceipts} receipt(s). Check console for detailed results.`);
    setShowSuccess(true);
  };

  // Helper function to add history entry
  const addHistoryEntry = (entry) => {
    const newHistory = historyManager.addEntry(entry, userEmail);
    setHistory(newHistory);
  };

  // Handle rule creation from history entry
  const handleCreateRuleFromHistory = (historyEntry) => {
    try {
      setSelectedHistoryEntry(historyEntry);
      setModalOpen(true);
    } catch (error) {
      setErrorMessage(error.message);
      setShowError(true);
    }
  };

  // Handle rule creation or update
  const handleCreateRule = (rule, ruleId = null) => {
    try {
      let updatedRules;
      
      if (ruleId) {
        // Update existing rule
        updatedRules = rulesManager.updateRule(ruleId, rule);
      } else {
        // Create new rule
        updatedRules = rulesManager.addRule(rule);
      }
      
      // Force a re-render by creating a new array
      setRules([...updatedRules]);
      // Force filter section to re-render
      setIsFilterOpen(false);
      setTimeout(() => setIsFilterOpen(true), 0);
    } catch (error) {
      setErrorMessage(error.message);
      setShowError(true);
    }
  };

  // Handle rule order change
  const handleRuleOrderChange = (newRules) => {
    const updatedRules = rulesManager.updateRuleOrder(newRules);
    // Force a re-render by creating a new array
    setRules([...updatedRules]);
  };

  // Handle applying a single rule
  const handleApplyRule = (rule) => {
    // First apply the filters
    const filteredRows = tableData.filter(row => {
      if (rule.filters.searchText && !`${row.date} ${row.amount} ${row.description} ${row.description2} ${row.comments} ${row.tags.join(' ')}`.toLowerCase().includes(rule.filters.searchText.toLowerCase())) {
        return false;
      }
      if (rule.filters.tags?.length > 0 && !rule.filters.tags.every(tag => row.tags.includes(tag))) {
        return false;
      }
      if (rule.filters.description && !row.description.toLowerCase().includes(rule.filters.description.toLowerCase())) {
        return false;
      }
      if (rule.filters.description2 && !row.description2.toLowerCase().includes(rule.filters.description2.toLowerCase())) {
        return false;
      }
      if (rule.filters.comments && !row.comments.toLowerCase().includes(rule.filters.comments.toLowerCase())) {
        return false;
      }
      if (rule.filters.filterStartDate || rule.filters.filterEndDate) {
        const rowDate = new Date(row.date).getTime();
        if (rule.filters.filterStartDate && rowDate < new Date(rule.filters.filterStartDate).getTime()) {
          return false;
        }
        if (rule.filters.filterEndDate && rowDate > new Date(rule.filters.filterEndDate).getTime()) {
          return false;
        }
      }
      const amount = parseFloat(row.amount);
      if (amount < rule.filters.filterMinAmount || amount > rule.filters.filterMaxAmount) {
        return false;
      }
      if (!rule.filters.tax[row.tax]) {
        return false;
      }
      if (rule.filters.showUntagged && row.tags.length > 0) {
        return false;
      }
      return true;
    });

    // Then apply each action in the rule
    const filteredIds = new Set(filteredRows.map(row => row.id));
    
    setTableData(prevData => {
      let newData = [...prevData];
      
      rule.actions.forEach(action => {
        switch (action.action) {
          case 'addTag':
            newData = bulkActions.bulkAddTag(newData, filteredIds, action.value);
            setAllTags(prev => new Set([...prev, action.value]));
            break;

          case 'removeTag':
            newData = bulkActions.bulkRemoveTag(newData, filteredIds, action.value);
            break;

          case 'tickTax':
            newData = bulkActions.bulkTickTax(newData, filteredIds);
            break;

          case 'untickTax':
            newData = bulkActions.bulkUntickTax(newData, filteredIds);
            break;

          case 'comment':
            newData = bulkActions.bulkSetComment(newData, filteredIds, action.value);
            break;

          case 'clearComment':
            newData = bulkActions.bulkClearComment(newData, filteredIds);
            break;
            
          default:
            console.warn(`Unrecognized action type: ${action.action}`);
            break;
        }
      });

      saveDataAndFilters(newData);
      return newData;
    });

    // Add history entry for rule application
    addHistoryEntry(historyManager.createRuleEntry(
      rule.name,
      rule.filters,
      rule.actions,
      filteredRows.length,
    ));
  };

  // Handle checkbox changes - optimized with functional updates
  const handleTaxChange = (id) => {
    setTableData(prevData => {
      const newData = [...prevData];
      const index = newData.findIndex(row => row.id === id);
      if (index === -1) return prevData;

      const newValue = !newData[index].tax;
      newData[index] = {
        ...newData[index],
        tax: newValue
      };

      // Record history for tax change
      addHistoryEntry(historyManager.createSingleChangeEntry(
        newData[index],
        {
          field: 'tax',
          valueBefore: (!newValue ? 'Yes' : 'No'),
          valueAfter: (newValue ? 'Yes' : 'No')
        },
      ));

      saveDataAndFilters(newData);
      return newData;
    });
  };

  // Handle comment changes with immediate updates
  const handleCommentChange = (id, value) => {
    setTableData(prevData => {
      const newData = [...prevData];
      const index = newData.findIndex(row => row.id === id);
      if (index === -1) return prevData;

      const oldComment = newData[index].comments || '';
      newData[index] = {
        ...newData[index],
        comments: value
      };

      // Only record history if the comment actually changed
      if (oldComment !== value) {
        addHistoryEntry(historyManager.createSingleChangeEntry(
          newData[index],
          {
            field: 'comment',
            valueBefore: oldComment,
            valueAfter: value
          },
        ));
      }

      // Save immediately
      saveDataAndFilters(newData);
      return newData;
    });
  };

  // Calculate min and max amounts and dates for the entire dataset
  const { minTableAmount, maxTableAmount, minTableDate, maxTableDate } = useMemo(() => {
    if (!tableData.length) return { minTableAmount: 0, maxTableAmount: 0, minTableDate: new Date(), maxTableDate: new Date() };
    const amounts = tableData.map(row => parseFloat(row.amount));
    const dates = tableData.map(row => new Date(row.date));
    return {
      minTableAmount: Math.floor(Math.min(...amounts)),
      maxTableAmount: Math.ceil(Math.max(...amounts)),
      minTableDate: new Date(Math.min(...dates)),
      maxTableDate: new Date(Math.max(...dates))
    };
  }, [tableData]);

  // Filter the table data based on current filters
  const filteredData = useMemo(() => {
    return filterTransactionData(tableData, filters);
  }, [tableData, filters]);

  // Handle bulk actions
  const handleBulkAction = (action, value) => {
    if(value) value = value.toLowerCase().trim();
    setTableData(prevData => {
      const filteredIds = new Set(filteredData.map(row => row.id));
      const affectedRows = filteredData.length;
      let newData = [...prevData];

      switch (action) {
        case 'addTag':
          newData = bulkActions.bulkAddTag(newData, filteredIds, value);
          setAllTags(prev => new Set([...prev, value]));
          addHistoryEntry(historyManager.createBulkActionEntry(
            {
              action: 'addTag',
              value: value,
            },
            affectedRows,
            filters
          ));
          break;

        case 'removeTag':
          newData = bulkActions.bulkRemoveTag(newData, filteredIds, value);
          addHistoryEntry(historyManager.createBulkActionEntry(
            {
              action: 'removeTag',
              value: value,
            },
            affectedRows,
            filters
          ));
          break;

        case 'tickTax':
          newData = bulkActions.bulkTickTax(newData, filteredIds);
          addHistoryEntry(historyManager.createBulkActionEntry(
            {
              action: 'tickTax',
              value: 'Yes',
            },
            affectedRows,
            filters
          ));
          break;

        case 'untickTax':
          newData = bulkActions.bulkUntickTax(newData, filteredIds);
          addHistoryEntry(historyManager.createBulkActionEntry(
            {
              action: 'untickTax',
              value: 'No',
            },
            affectedRows,
            filters
          ));
          break;

        case 'setComment':
          newData = bulkActions.bulkSetComment(newData, filteredIds, value);
          addHistoryEntry(historyManager.createBulkActionEntry(
            {
              action: 'setComment',
              value: value,
            },
            affectedRows,
            filters
          ));
          break;

        case 'clearComment':
          newData = bulkActions.bulkClearComment(newData, filteredIds);
          addHistoryEntry(historyManager.createBulkActionEntry(
            {
              action: 'clearComment',
              value: '',
            },
            affectedRows,
            filters
          ));
          break;  

        default:
          return prevData;
      }

      saveDataAndFilters(newData);
      return newData;
    });
  };

  return (
    <Box sx={{ p: 3 }}>
      <ErrorSnackbar
        open={showError}
        message={errorMessage}
        onClose={() => setShowError(false)}
      />
      
      <ErrorSnackbar
        open={showSuccess}
        message={successMessage}
        onClose={() => setShowSuccess(false)}
        severity="success"
      />

      <Typography variant="h6" gutterBottom>
        Tag your transactions
      </Typography>
      
      <ActionPanel 
        tableData={tableData} 
        filteredData={filteredData}
        onSaveTransactions={handleSaveTransactions}
        onReceiptUpload={handleReceiptUpload}
        onLoadData={handleLoadData}
      />
      
      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
          <CircularProgress size={60} thickness={4} />
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-start', mb: 1 }}>
            <Button 
              variant="outlined" 
              onClick={() => setIsFilterOpen(!isFilterOpen)}
            >
              {isFilterOpen ? 'Hide Filters' : 'Show Filters'}
            </Button>
          </Box>
          
          <FilterSection
            filters={filters}
            setFilters={(newFilters) => {
              setFilters(newFilters);
            }}
            allTags={allTags}
            minAmount={minTableAmount}
            maxAmount={maxTableAmount}
            minDate={minTableDate}
            maxDate={maxTableDate}
            isOpen={isFilterOpen}
            onBulkAction={handleBulkAction}
            rules={rules}
            onRuleApply={handleApplyRule}
            onRuleCreate={handleCreateRule}
            onRuleOrderChange={handleRuleOrderChange}
          />
          
          <TransactionGrid
            filteredData={filteredData}
            allTags={allTags}
            setAllTags={setAllTags}
            setTableData={setTableData}
            handleTaxChange={handleTaxChange}
            handleCommentChange={handleCommentChange}
            saveTableData={saveDataAndFilters}
            addHistoryEntry={addHistoryEntry}
            historyManager={historyManager}
          />
        </Box>
      )}

      {/* History */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-start', mt: 2 }}>
        <Button 
          variant="outlined" 
          onClick={() => setIsHistoryOpen(!isHistoryOpen)}
        >
          {isHistoryOpen ? 'Hide History' : 'Show History'}
        </Button>
      </Box>
      <ChangeHistory 
        history={history} 
        onCreateRule={handleCreateRuleFromHistory}
        isOpen={isHistoryOpen}
      />
      
      {/* Rule Modal */}
      <RuleModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        initialFilters={selectedHistoryEntry ? {
          ...selectedHistoryEntry.filters,
          filterStartDate: selectedHistoryEntry.filters.filterStartDate ? new Date(selectedHistoryEntry.filters.filterStartDate) : null,
          filterEndDate: selectedHistoryEntry.filters.filterEndDate ? new Date(selectedHistoryEntry.filters.filterEndDate) : null,
          minDate: selectedHistoryEntry.filters.minDate ? new Date(selectedHistoryEntry.filters.minDate) : null,
          maxDate: selectedHistoryEntry.filters.maxDate ? new Date(selectedHistoryEntry.filters.maxDate) : null
        } : filters}
        initialActions={selectedHistoryEntry ? [selectedHistoryEntry.action] : []}
        allTags={allTags}
        onSave={handleCreateRule}
        minAmount={minTableAmount}
        maxAmount={maxTableAmount}
        minDate={minTableDate}
        maxDate={maxTableDate}
      />
    </Box>
  );
};

export default TagTransactions;